import { App, Form, Input, Modal, Select, Space, Switch } from "antd";
import { CloseSquareFilled, SaveFilled } from '@ant-design/icons';
import { useEffect, useMemo } from "react";
import execute from "hooks/execute";
import { protectedResources } from "config/msalConfig";

const ALL_MODULES_VALUE = "__ALL__";

const parseModules = (modulesText) => (modulesText || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const EditRoleEditor = ({ isOpen, role, availableModules = [], onFinished, onCancelled }) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();

    const moduleOptions = useMemo(() => {
        const list = (availableModules || []).map((module) => ({
            value: module.shortName || module.value,
            label: module.shortName || module.value
        }));
        return [{ value: ALL_MODULES_VALUE, label: 'All' }, ...list];
    }, [availableModules]);

    const allModuleShortNames = useMemo(
        () => moduleOptions.filter((option) => option.value !== ALL_MODULES_VALUE).map((option) => option.value),
        [moduleOptions]
    );

    const handleModulesChange = (selectedValues) => {
        const selected = selectedValues || [];
        if (selected.includes(ALL_MODULES_VALUE)) {
            form.setFieldValue('modules', allModuleShortNames);
            return;
        }

        form.setFieldValue('modules', selected.filter((value) => value !== ALL_MODULES_VALUE));
    };

    const handleSave = async (values) => {
        try {
            const response = await execute('PUT', `${protectedResources.api.baseEndPoint}/customerRoles/${role.Id}/status-modules`, {
                status: values.status ? 1 : 0,
                modules: values.modules
            });

            if (response?.success) {
                message.success('Customer role updated successfully');
                onFinished(response.data);
            } else {
                message.error(response?.message || 'Failed to update customer role');
            }
        } catch (error) {
            message.error(error?.response?.data?.message || error.message || 'Failed to update customer role');
        }
    };

    useEffect(() => {
        if (isOpen && role) {
            form.setFieldsValue({
                role_name: role.Name,
                status: Number(role.Status) === 1,
                modules: parseModules(role.Modules)
            });
        }
    }, [isOpen, role, form]);

    return (
        <Modal
            open={isOpen}
            title="Edit Customer Role"
            okText={<Space><SaveFilled />Save</Space>}
            cancelText={<Space><CloseSquareFilled />Cancel</Space>}
            okButtonProps={{ autoFocus: true, htmlType: 'submit' }}
            onCancel={onCancelled}
            destroyOnClose
            modalRender={(dom) => (
                <Form
                    layout="vertical"
                    form={form}
                    name="editRoleForm"
                    clearOnDestroy
                    onFinish={handleSave}
                >
                    {dom}
                </Form>
            )}
        >
            <Form.Item name="role_name" label="Role Name">
                <Input disabled />
            </Form.Item>

            <Form.Item name="status" label="Status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item
                name="modules"
                label="Modules"
                rules={[{ required: true, message: 'Select at least one module' }]}
            >
                <Select
                    mode="multiple"
                    allowClear
                    placeholder="Select modules"
                    options={moduleOptions}
                    onChange={handleModulesChange}
                />
            </Form.Item>
        </Modal>
    );
};

export default EditRoleEditor;
